import React, { useEffect, useMemo, useState } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  Table,
  Image,
  Form,
} from "react-bootstrap";
import { Shoporder, Shoptopbar } from "Components/ShopTopBar";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ShopingAddress } from "./ShoppingAddress";
import { CommonService } from "Components/CommonService";
import { useGetStoreCartQuery } from "../../redux/services/store/storeCartApi";
import { useGetStoreMeQuery } from "../../redux/services/store/storeAuthApi";
import { useGetStoreMyAddressesQuery } from "../../redux/services/store/storeAccountApi";
import {
  useGetStoreShippingOptionsMutation,
  type StoreShippingOptionRow,
} from "../../redux/services/store/storeShippingApi";
import { loadBuyNowCheckoutDraft } from "../../Common/buyNowCheckout";

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseQty = (v: any): number => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.round(n));
};

const cleanText = (value: unknown): string => {
  const text = String(value ?? "").trim();
  return text;
};

const getVariantSummary = (product: any): string => {
  if (product?.variant_name) {
    return cleanText(product.variant_name);
  }

  const attrs = product?.attributes_json;
  if (!attrs || typeof attrs !== "object") {
    return "";
  }

  return Object.entries(attrs)
    .map(([key, value]) => {
      const label = cleanText(key);
      const content = cleanText(value);
      if (!label || !content) return "";
      return `${label}: ${content}`;
    })
    .filter(Boolean)
    .join(", ");
};

const getCustomizationSummary = (item: any): string => {
  const color = cleanText(item?.customization_color);
  const note = cleanText(item?.customization_note);
  const enabled = Boolean(item?.customization_enabled || color || note);
  if (!enabled) return "";

  const parts = [];
  if (color) parts.push(`Custom color: ${color}`);
  if (note) parts.push(`Custom note: ${note}`);
  return parts.length ? parts.join(" | ") : "Customization requested";
};

const checkoutSkeletonBaseStyle: React.CSSProperties = {
  background: "linear-gradient(90deg, #f1e7df 25%, #fbf7f3 50%, #f1e7df 75%)",
  backgroundSize: "200% 100%",
  animation: "checkoutSkeletonShimmer 1.4s ease infinite",
  borderRadius: 14,
};

const CheckoutSkeletonStyles = () => (
  <style>
    {`
      @keyframes checkoutSkeletonShimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}
  </style>
);

const CheckoutTableBodySkeletonRows = ({ rows = 3 }: { rows?: number }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <tr key={`checkout-skeleton-row-${index}`}>
        <td className="text-start">
          <div className="d-flex align-items-center gap-2">
            <div style={{ ...checkoutSkeletonBaseStyle, width: 38, height: 38, borderRadius: 12 }} />
            <div className="flex-grow-1">
              <div
                style={{
                  ...checkoutSkeletonBaseStyle,
                  height: 15,
                  width: index % 2 === 0 ? "48%" : "58%",
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...checkoutSkeletonBaseStyle,
                  height: 12,
                  width: index % 2 === 0 ? "30%" : "36%",
                  borderRadius: 10,
                }}
              />
            </div>
          </div>
        </td>
        <td>
          <div style={{ ...checkoutSkeletonBaseStyle, height: 14, width: 76, marginInline: "auto" }} />
        </td>
        <td>
          <div style={{ ...checkoutSkeletonBaseStyle, height: 14, width: 54, marginInline: "auto" }} />
        </td>
        <td className="text-end">
          <div style={{ ...checkoutSkeletonBaseStyle, height: 14, width: 92, marginLeft: "auto" }} />
        </td>
      </tr>
    ))}
  </>
);

const CheckoutSectionSkeletonCard = ({ height = 120 }: { height?: number }) => (
  <Card className="mt-4">
    <Card.Body>
      <div style={{ ...checkoutSkeletonBaseStyle, height: 16, width: "32%", marginBottom: 16 }} />
      <div style={{ ...checkoutSkeletonBaseStyle, height, width: "100%" }} />
    </Card.Body>
  </Card>
);

const CheckoutDeliverySkeleton = () => (
  <Card className="mt-4">
    <Card.Header className="border-bottom-dashed">
      <div style={{ ...checkoutSkeletonBaseStyle, height: 18, width: 140 }} />
    </Card.Header>
    <Card.Body>
      {Array.from({ length: 2 }).map((_, index) => (
        <div
          key={`checkout-delivery-skeleton-${index}`}
          className="border rounded p-3 mb-3"
        >
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div className="flex-grow-1">
              <div
                style={{
                  ...checkoutSkeletonBaseStyle,
                  height: 15,
                  width: index === 0 ? "34%" : "42%",
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...checkoutSkeletonBaseStyle,
                  height: 12,
                  width: index === 0 ? "48%" : "56%",
                  borderRadius: 10,
                }}
              />
            </div>
            <div style={{ ...checkoutSkeletonBaseStyle, height: 16, width: 70 }} />
          </div>
        </div>
      ))}
    </Card.Body>
  </Card>
);

const CheckoutSidebarSkeleton = () => (
  <div className="sticky-side-div">
    <Card>
      <Card.Body>
        <div style={{ ...checkoutSkeletonBaseStyle, height: 18, width: 120, marginBottom: 18 }} />
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`checkout-summary-line-${index}`}
            className="d-flex align-items-center justify-content-between mb-3"
          >
            <div style={{ ...checkoutSkeletonBaseStyle, height: 14, width: index === 3 ? "42%" : "34%" }} />
            <div style={{ ...checkoutSkeletonBaseStyle, height: 14, width: 74 }} />
          </div>
        ))}
      </Card.Body>
    </Card>
    <div className="hstack gap-2 mt-3">
      <div style={{ ...checkoutSkeletonBaseStyle, height: 42, width: "100%", borderRadius: 999 }} />
      <div style={{ ...checkoutSkeletonBaseStyle, height: 42, width: "100%", borderRadius: 999 }} />
    </div>
  </div>
);

const CheckoutSkeleton = () => (
  <>
    <CheckoutSkeletonStyles />
    <Shoptopbar title="Checkout" page="Checkout" />
    <section className="section">
      <Container>
        <Row>
          <Col lg={8}>
            <Card>
              <Card.Body>
                <div className="table-responsive table-card">
                  <Table className="align-middle table-borderless table-nowrap text-center mb-0">
                    <thead>
                      <tr>
                        <th scope="col">Product</th>
                        <th scope="col">Rate</th>
                        <th scope="col">Order ID</th>
                        <th scope="col">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <CheckoutTableBodySkeletonRows rows={4} />
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <CheckoutSectionSkeletonCard height={112} />
            <CheckoutSectionSkeletonCard height={112} />
            <CheckoutDeliverySkeleton />
          </Col>

          <Col lg={4}>
            <CheckoutSidebarSkeleton />
          </Col>
        </Row>
      </Container>
    </section>
    <CommonService />
  </>
);

const Checkout = () => {
  const location = useLocation();
  const buyNowModeRequested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("mode") === "buy-now";
  }, [location.search]);
  const buyNowDraft = useMemo(
    () => (buyNowModeRequested ? loadBuyNowCheckoutDraft() : null),
    [buyNowModeRequested]
  );

  const {
    data: me,
    isLoading: isMeLoading,
    isFetching: isMeFetching,
    isSuccess: isMeSuccess,
  } = useGetStoreMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCheckingAuth = isMeLoading || isMeFetching;
  const isLoggedIn = isMeSuccess && !!me?.id;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchCart,
  } = useGetStoreCartQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: addressesData,
    isLoading: isAddressLoading,
    isFetching: isAddressFetching,
  } = useGetStoreMyAddressesQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const [
    getStoreShippingOptions,
    {
      isLoading: isShippingOptionsLoading,
      isError: isShippingOptionsError,
    },
  ] = useGetStoreShippingOptionsMutation();

  const [shippingOptions, setShippingOptions] = useState<StoreShippingOptionRow[]>([]);
  const [selectedDeliveryTypeId, setSelectedDeliveryTypeId] = useState<number | null>(null);

  const buyNowPreviewItem = useMemo(() => {
    if (!buyNowDraft) return null;

    const qty = parseQty(buyNowDraft.qty);
    const unitPrice = toNum(buyNowDraft.unit_price);
    const lineTotal = unitPrice * qty;

    return {
      id: buyNowDraft.variant_id,
      qty,
      unit_price: unitPrice,
      line_total: lineTotal,
      customization_enabled: !!buyNowDraft.customization_enabled,
      customization_color: buyNowDraft.customization_color || null,
      customization_note: buyNowDraft.customization_note || null,
      product: {
        product_id: buyNowDraft.product_id ?? 0,
        name: buyNowDraft.product_name,
        slug: buyNowDraft.product_slug,
        variant_id: buyNowDraft.variant_id,
        image: {
          cdn_url: buyNowDraft.image_url || null,
          alt_text: buyNowDraft.image_alt || buyNowDraft.product_name,
        },
        variant_name: buyNowDraft.variant_name || null,
      },
    };
  }, [buyNowDraft]);

  const cartItems = (data as any)?.items ?? [];
  const items = useMemo(
    () => (buyNowPreviewItem ? [buyNowPreviewItem] : cartItems),
    [buyNowPreviewItem, cartItems]
  );
  const currency = buyNowDraft?.currency_code || (data as any)?.cart?.currency_code || "USD";

  const cartSubtotal = useMemo(() => toNum((data as any)?.totals?.subtotal), [data]);
  const cartDiscount = useMemo(() => toNum((data as any)?.totals?.discount), [data]);
  const buyNowSubtotal = useMemo(() => {
    if (!buyNowDraft) return 0;

    const qty = parseQty(buyNowDraft.qty);
    const unitPrice = toNum(buyNowDraft.unit_price);
    const compareAtPrice = toNum(buyNowDraft.compare_at_price);

    if (compareAtPrice > unitPrice && compareAtPrice > 0) {
      return compareAtPrice * qty;
    }

    return unitPrice * qty;
  }, [buyNowDraft]);
  const buyNowDiscount = useMemo(() => {
    if (!buyNowDraft) return 0;

    const qty = parseQty(buyNowDraft.qty);
    const unitPrice = toNum(buyNowDraft.unit_price);
    const compareAtPrice = toNum(buyNowDraft.compare_at_price);

    if (compareAtPrice > unitPrice && compareAtPrice > 0) {
      return (compareAtPrice - unitPrice) * qty;
    }

    return 0;
  }, [buyNowDraft]);

  const subtotal = useMemo(
    () => (buyNowDraft ? buyNowSubtotal : cartSubtotal),
    [buyNowDraft, buyNowSubtotal, cartSubtotal]
  );
  const discount = useMemo(
    () => (buyNowDraft ? buyNowDiscount : cartDiscount),
    [buyNowDraft, buyNowDiscount, cartDiscount]
  );

  const totalQty = useMemo(() => {
    if (buyNowDraft) return parseQty(buyNowDraft.qty);
    return items.reduce((sum: number, it: any) => sum + parseQty(it?.qty), 0);
  }, [buyNowDraft, items]);

  const totalWeightKg = 0;

  const addresses = useMemo(() => {
    const rows = Array.isArray(addressesData) ? addressesData : [];
    return rows.filter((item: any) => !!item?.is_active);
  }, [addressesData]);

  const defaultShippingAddress = useMemo(() => {
    return addresses.find((item: any) => !!item?.is_default_shipping) || null;
  }, [addresses]);

  const defaultBillingAddress = useMemo(() => {
    return (
      addresses.find((item: any) => !!item?.is_default_billing) ||
      defaultShippingAddress ||
      null
    );
  }, [addresses, defaultShippingAddress]);

  useEffect(() => {
    if (isLoggedIn) {
      refetchCart();
    }
  }, [isLoggedIn, refetchCart]);

  useEffect(() => {
    if (!isLoggedIn) {
      setShippingOptions([]);
      setSelectedDeliveryTypeId(null);
      return;
    }

    const loadShippingOptions = async () => {
      if (!defaultShippingAddress?.country_code || !defaultShippingAddress?.state || totalQty <= 0) {
        setShippingOptions([]);
        setSelectedDeliveryTypeId(null);
        return;
      }

      try {
          const result = await getStoreShippingOptions({
            country_code: String(defaultShippingAddress.country_code || "").toUpperCase(),
            state_name: defaultShippingAddress.state || null,
            total_qty: totalQty,
            total_weight_kg: totalWeightKg,
            subtotal_amount: subtotal,
          }).unwrap();

        const rows = Array.isArray(result) ? result : [];
        setShippingOptions(rows);

        setSelectedDeliveryTypeId((prev) => {
          if (prev && rows.some((x) => Number(x.delivery_type_id) === Number(prev))) {
            return prev;
          }
          return rows.length ? Number(rows[0].delivery_type_id) : null;
        });
      } catch (e) {
        console.log(e);
        setShippingOptions([]);
        setSelectedDeliveryTypeId(null);
      }
    };

    loadShippingOptions();
  }, [
    isLoggedIn,
    defaultShippingAddress?.country_code,
    defaultShippingAddress?.state,
    totalQty,
    subtotal,
    getStoreShippingOptions,
  ]);

  const selectedShippingOption = useMemo(() => {
    if (!selectedDeliveryTypeId) return null;
    return (
      shippingOptions.find(
        (opt) => Number(opt.delivery_type_id) === Number(selectedDeliveryTypeId)
      ) || null
    );
  }, [shippingOptions, selectedDeliveryTypeId]);

  const charge = useMemo(() => {
    if (selectedShippingOption?.shipping_amount != null) {
      return toNum(selectedShippingOption.shipping_amount);
    }
    return 0;
  }, [selectedShippingOption]);

  const total = useMemo(() => {
    return subtotal + charge - discount;
  }, [subtotal, charge, discount]);

  const canContinuePayment =
    isLoggedIn &&
    items.length > 0 &&
    !!defaultShippingAddress?.id &&
    !!selectedShippingOption &&
    !isShippingOptionsLoading;

  if (isCheckingAuth) {
    return <CheckoutSkeleton />;
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/auth-signin-basic"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <CheckoutSkeletonStyles />
      <Shoptopbar title="Checkout" page="Checkout" />
      <section className="section">
        <Container>
          <Row>
            <Col lg={8}>
              <Card>
                <Card.Body>
                  <div className="table-responsive table-card">
                    <Table className="align-middle table-borderless table-nowrap text-center mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Product</th>
                          <th scope="col">Rate</th>
                          <th scope="col">Order ID</th>
                          <th scope="col">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!buyNowDraft && isError ? (
                          <tr>
                            <td colSpan={4} className="text-danger py-4">
                              Failed to load checkout items
                            </td>
                          </tr>
                        ) : !buyNowDraft && (isLoading || isFetching) ? (
                          <CheckoutTableBodySkeletonRows rows={3} />
                        ) : items.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="py-4 text-muted">
                              {buyNowModeRequested ? "No Buy Now item selected" : "Your cart is empty"}
                            </td>
                          </tr>
                        ) : (
                          items.map((it: any, inx: number) => {
                            const p = it?.product || {};
                            const title = p?.name || "";
                            const img =
                              p?.image?.cdn_url || "https://via.placeholder.com/48x48.png?text=IMG";

                            const qty = parseQty(it?.qty);
                            const rate = toNum(it?.unit_price);
                            const amount = it?.line_total != null ? toNum(it?.line_total) : rate * qty;
                            const description = [getVariantSummary(p), getCustomizationSummary(it)]
                              .filter(Boolean)
                              .join(" | ");

                            return (
                              <tr key={it?.id ?? `checkout-item-${inx}`}>
                                <td className="text-start">
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="avatar-sm flex-shrink-0">
                                      <div className="avatar-title bg-light-subtle rounded-3">
                                        <Image src={img} alt="" className="avatar-xs" />
                                      </div>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6>{title}</h6>
                                      <p className="text-muted mb-0">{description || `Qty: ${qty}`}</p>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {currency} {rate.toFixed(2)}
                                </td>
                                <td>{it?.id ? `0${it.id}` : `0${inx + 1}`}</td>
                                <td className="text-end">
                                  {currency} {amount.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>

              <ShopingAddress
                title="Select or add an address"
                HomeAdd="Home Address"
                officeAdd="Office Address"
                from="checkout"
              />
              <ShopingAddress title="Billing Address" from="checkout" />

                <Card className="mt-4">
                  <Card.Header className="border-bottom-dashed">
                    <h5 className="card-title mb-0 fs-15">Delivery Method</h5>
                  </Card.Header>
                  <Card.Body>
                    {isAddressLoading || isAddressFetching ? (
                      <>
                        <div style={{ ...checkoutSkeletonBaseStyle, height: 16, width: "42%", marginBottom: 14 }} />
                        <div style={{ ...checkoutSkeletonBaseStyle, height: 84, width: "100%" }} />
                      </>
                    ) : !defaultShippingAddress ? (
                      <div className="text-muted">
                        Please select or add a default shipping address first.
                      </div>
                    ) : isShippingOptionsLoading ? (
                      <>
                        {Array.from({ length: 2 }).map((_, index) => (
                          <div
                            key={`checkout-inline-delivery-skeleton-${index}`}
                            className="border rounded p-3 mb-3"
                          >
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div className="flex-grow-1">
                                <div
                                  style={{
                                    ...checkoutSkeletonBaseStyle,
                                    height: 15,
                                    width: index === 0 ? "36%" : "44%",
                                    marginBottom: 10,
                                  }}
                                />
                                <div
                                  style={{
                                    ...checkoutSkeletonBaseStyle,
                                    height: 12,
                                    width: index === 0 ? "52%" : "58%",
                                    borderRadius: 10,
                                  }}
                                />
                              </div>
                              <div style={{ ...checkoutSkeletonBaseStyle, height: 16, width: 70 }} />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : isShippingOptionsError ? (
                      <div className="text-danger">
                        Failed to load delivery methods for selected address.
                    </div>
                  ) : shippingOptions.length === 0 ? (
                    <div className="text-muted">
                      No delivery method available for{" "}
                      <strong>
                        {defaultShippingAddress.state}, {defaultShippingAddress.country_code}
                      </strong>
                      .
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {shippingOptions.map((option) => {
                        const checked =
                          Number(selectedDeliveryTypeId) === Number(option.delivery_type_id);

                        return (
                          <div
                            key={option.delivery_type_id}
                            className={`border rounded p-3 ${checked ? "border-primary bg-light" : ""}`}
                          >
                            <Form.Check
                              type="radio"
                              id={`delivery-type-${option.delivery_type_id}`}
                              name="delivery_type_id"
                              checked={checked}
                              onChange={() => setSelectedDeliveryTypeId(Number(option.delivery_type_id))}
                              label={
                                <div className="d-flex justify-content-between align-items-start w-100">
                                  <div>
                                    <div className="fw-semibold">{option.delivery_type_name}</div>
                                    <div className="text-muted fs-13">
                                      {option.matched_rule_name}
                                    </div>
                                  </div>
                                  <div className="fw-semibold">
                                    {currency} {toNum(option.shipping_amount).toFixed(2)}
                                  </div>
                                </div>
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <div className="sticky-side-div">
                <Shoporder
                  subtotal={subtotal}
                  dic={discount}
                  charge={charge}
                  total={total}
                  currency={currency}
                />

                {selectedShippingOption ? (
                  <Card className="mt-3">
                    <Card.Body>
                      <div className="fw-semibold mb-1">Selected Delivery</div>
                      <div>{selectedShippingOption.delivery_type_name}</div>
                      <div className="text-muted fs-13">{selectedShippingOption.matched_rule_name}</div>
                    </Card.Body>
                  </Card>
                ) : null}

                <div className="hstack gap-2 justify-content-between justify-content-end mt-3">
                  <Link to="/shop/shopingcard" className="btn btn-hover btn-soft-info w-100">
                    Back To Cart <i className="ri-arrow-right-line label-icon align-middle ms-1"></i>
                  </Link>
                  <Link
                    to="/shop/payment"
                    state={{
                      shipping_address_id: defaultShippingAddress?.id ?? null,
                      billing_address_id: defaultBillingAddress?.id ?? null,
                      delivery_type_id: selectedShippingOption?.delivery_type_id ?? null,
                      delivery_type_name: selectedShippingOption?.delivery_type_name ?? null,
                      shipping_amount: charge,
                      shipping_currency: currency,
                      shipping_rule_id: selectedShippingOption?.matched_rule_id ?? null,
                      shipping_rule_name: selectedShippingOption?.matched_rule_name ?? null,
                      subtotal: subtotal,
                      discount: discount,
                      total: total,
                      checkout_mode: buyNowDraft ? "buy-now" : "cart",
                      buy_now_item: buyNowDraft
                        ? {
                            variant_id: buyNowDraft.variant_id,
                            qty: parseQty(buyNowDraft.qty),
                            customization_enabled: !!buyNowDraft.customization_enabled,
                            customization_color: buyNowDraft.customization_color || null,
                            customization_note: buyNowDraft.customization_note || null,
                          }
                        : null,
                    }}
                    className={`btn btn-hover btn-primary w-100 ${canContinuePayment ? "" : "disabled"}`}
                    onClick={(e) => {
                      if (!canContinuePayment) e.preventDefault();
                    }}
                  >
                    Continue Payment
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <CommonService />
    </React.Fragment>
  );
};

export default Checkout;
